import { Injectable, signal, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface DailyUsage {
  date: string;
  bytes: number;
  download: number; // in GB
  upload: number;   // in GB
}

@Injectable({
  providedIn: 'root'
})
export class InternetService {
  private platformId = inject(PLATFORM_ID);
  
  public speed = signal<number>(0);
  public isOnline = signal<boolean>(true);
  public totalUsage = signal<number>(0); // in bytes
  public dailyUsage = signal<DailyUsage[]>([]);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initSpeedMonitor();
      this.initUsageData();
      this.initConnectivityMonitor();
    }
  }

  private initConnectivityMonitor() {
    this.isOnline.set(navigator.onLine);
    
    window.addEventListener('online', () => {
      this.isOnline.set(true);
      // Trigger a global refresh check on reconnection
      window.dispatchEvent(new CustomEvent('pwa-sync-request'));
    });
    
    window.addEventListener('offline', () => {
      this.isOnline.set(false);
    });
  }

  private initSpeedMonitor() {
    this.updateSpeed();
    
    // Some browsers support navigator.connection
    const nav: unknown = navigator;
    const navConn = (nav as { connection?: { addEventListener: (event: string, cb: () => void) => void } }).connection;
    if (navConn) {
      navConn.addEventListener('change', () => this.updateSpeed());
    }

    // Ping test fallback / update interval
    setInterval(() => {
      this.updateSpeed();
      this.simulateUsage();
    }, 5000);
  }

  private updateSpeed() {
    const nav: unknown = navigator;
    const navConn = (nav as { connection?: { downlink?: number } }).connection;
    if (navConn && navConn.downlink) {
      // downlink is in Mbps
      this.speed.set(navConn.downlink);
    } else {
      // simulate speed between 10-50 Mbps if API not available
      const randomSpeed = Math.floor(Math.random() * 40) + 10;
      this.speed.set(randomSpeed + Math.random());
    }
  }

  private initUsageData() {
    const stored = localStorage.getItem('kicgle_internet_usage');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        this.dailyUsage.set(parsed.daily || []);
        this.totalUsage.set(parsed.total || 0);
      } catch {
        this.generateMockUsage();
      }
    } else {
      this.generateMockUsage();
    }
  }

  private generateMockUsage() {
    // Generate last 7 days of mock data
    const daily: DailyUsage[] = [];
    let total = 0;
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const bytes = Math.floor(Math.random() * 500 * 1024 * 1024) + 100 * 1024 * 1024; // 100MB - 600MB
      
      const totalGb = bytes / (1024 * 1024 * 1024);
      daily.push({
        date: date.toISOString().split('T')[0],
        bytes,
        download: totalGb * 0.85,
        upload: totalGb * 0.15
      });
      total += bytes;
    }
    
    this.dailyUsage.set(daily);
    this.totalUsage.set(total);
    this.saveUsage();
  }

  private simulateUsage() {
    // Add random usage every 5 seconds (e.g. 100KB to 5MB)
    const newBytes = Math.floor(Math.random() * 5 * 1024 * 1024);
    
    const todayStr = new Date().toISOString().split('T')[0];
    const currentDaily = [...this.dailyUsage()];
    
    const todayIndex = currentDaily.findIndex(d => d.date === todayStr);
    const newGb = newBytes / (1024 * 1024 * 1024);
    
    if (todayIndex >= 0) {
      currentDaily[todayIndex].bytes += newBytes;
      currentDaily[todayIndex].download += newGb * 0.9;
      currentDaily[todayIndex].upload += newGb * 0.1;
    } else {
      currentDaily.push({
        date: todayStr,
        bytes: newBytes,
        download: newGb * 0.9,
        upload: newGb * 0.1
      });
      if (currentDaily.length > 30) {
        currentDaily.shift();
      }
    }

    this.dailyUsage.set(currentDaily);
    this.totalUsage.update(t => t + newBytes);
    this.saveUsage();
  }

  private saveUsage() {
    localStorage.setItem('kicgle_internet_usage', JSON.stringify({
      daily: this.dailyUsage(),
      total: this.totalUsage()
    }));
  }
}
